import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { scoreboardApi, championshipApi } from '../lib/api';
import { Play, Pause, Minus, X, RefreshCw, Loader2 } from 'lucide-react';

function formatDisplay(fullName) {
  if (!fullName) return '';
  const particles = ['de', 'da', 'do', 'dos', 'das'];
  const raw = fullName.trim().split(/\s+/);
  if (raw.length === 0) return '';
  const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  const first = cap(raw[0]);
  if (raw.length === 1) return first;
  const secondLower = raw[1].toLowerCase();
  if (particles.includes(secondLower)) {
    if (raw.length >= 3) return first + ' ' + cap(raw[1]) + ' ' + cap(raw[2]);
    return first + ' ' + cap(raw[1]);
  }
  return first + ' ' + cap(raw[1]);
}

function formatTime(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

/**
 * Quadro gigante de pontuação (imagem de referência):
 * - Clique em qualquer lugar do quadro  => +1 (alvo grande para o mesário)
 * - Botão pequeno "−" no canto inferior esquerdo
 * - Botão pequeno "×" (zerar) no canto superior direito
 */
function DisplayBox({ label, value, colorClass, darkText, onPlus, onMinus, onReset, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onPlus}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onPlus();
        }
      }}
      title="Clique para +1"
      className={`tela-sempre-escura relative aspect-square flex-1 max-h-full ${colorClass} rounded-2xl overflow-hidden cursor-pointer select-none transition-transform hover:brightness-110 active:scale-[0.98] flex items-center justify-center ${disabled ? 'opacity-70 cursor-default' : ''}`}
    >
      <span className={`absolute top-[5%] left-[6%] text-[clamp(0.5rem,1.05vw,1.05rem)] font-black uppercase whitespace-nowrap ${darkText ? 'text-black/50' : 'text-white/80'}`}>
        {label}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReset(); }}
        disabled={disabled}
        title="Zerar"
        className="absolute top-[4%] right-[4%] w-[clamp(1.1rem,2.3vw,2.3rem)] h-[clamp(1.1rem,2.3vw,2.3rem)] rounded-lg bg-black/25 hover:bg-black/50 text-white flex items-center justify-center"
      >
        <X size={20} />
      </button>
      <div className={`font-black leading-none whitespace-nowrap ${darkText ? 'text-black' : 'text-white'} text-[clamp(3.25rem,13.75vw,14.4rem)]`}>
        {value}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onMinus(); }}
        disabled={disabled || value <= 0}
        title="Diminuir (−1)"
        className="absolute bottom-[4%] left-[5%] w-[clamp(1.3rem,2.6vw,2.6rem)] h-[clamp(1.3rem,2.6vw,2.6rem)] rounded-lg bg-black/25 hover:bg-black/55 disabled:opacity-30 text-white flex items-center justify-center"
      >
        <Minus size={20} />
      </button>
    </div>
  );
}

function DisplayAthletePanel({ label, name, team, panelColor, textDark, boxes, disabled, onBump }) {
  return (
    <div className={`flex-1 flex items-stretch gap-[0.5vw] px-[0.5vw] pt-[0.4vw] min-h-0 ${panelColor}`}>
      <div className="flex-1 flex flex-col justify-center min-w-0 px-[2vw]">
        <div className={`text-[clamp(0.6rem,1.4vw,1.4rem)] font-black uppercase tracking-[0.35em] ${textDark ? 'text-black/40' : 'text-white/60'}`}>
          {label}
        </div>
        <h1 className={`font-black leading-none break-words ${textDark ? 'text-black' : 'text-white'} text-[clamp(1.8rem,5.2vw,6.5rem)]`}>
          {name || '—'}
        </h1>
        <div className={`mt-[0.6vw] font-semibold ${textDark ? 'text-black/60' : 'text-white/80'} text-[clamp(0.8rem,1.8vw,1.8rem)]`}>
          {team || ''}
        </div>
      </div>
      <div className="flex gap-[0.6vw] items-center w-[55%] pb-[0.4vw]">
        {boxes.map((b) => (
          <DisplayBox
            key={b.label}
            label={b.label}
            value={b.value}
            colorClass={b.color}
            darkText={b.darkText}
            onPlus={() => onBump(b.field, 1)}
            onMinus={() => onBump(b.field, -1)}
            onReset={() => onBump(b.field, -b.value)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

const ZERO_BOXES = (side) => [
  { label: 'PONTOS', field: side === 'a1' ? 'score1' : 'score2', value: 0, color: side === 'a1' ? 'bg-[#5CB85C]' : 'bg-[#B3B3B3]', darkText: side !== 'a1' },
  { label: 'VANTAGENS', field: side === 'a1' ? 'advantages1' : 'advantages2', value: 0, color: 'bg-[#FFF567]', darkText: true },
  { label: 'PUNIÇÕES', field: side === 'a1' ? 'penalties1' : 'penalties2', value: 0, color: 'bg-[#FF0000]', darkText: false },
];

export default function ScoreboardDisplay() {
  const [searchParams] = useSearchParams();
  const championshipId = searchParams.get('championship') || '';
  const mesa = searchParams.get('mesa') || 'Mesa 1';

  const [championshipName, setChampionshipName] = useState('');
  const [sessions, setSessions] = useState([]);
  const [syncing, setSyncing] = useState(true); // primeira carga (sempre sai após 1º poll ou timeout)
  const [reconnecting, setReconnecting] = useState(false); // falha de rede no polling
  const [tick, setTick] = useState(Date.now());
  // Âncora do cronômetro: baseElapsed + running + lastSync são atualizados a cada
  // poll de forma MONOTÔNICA (nunca regridem). O `elapsed_seconds` do servidor é
  // um snapshot de ~1s do painel do operador — usá-lo direto como base fazia o
  // relógio "voltar" (ex.: 2:58 -> 3:00) a cada poll de 2s.
  const [timerAnchor, setTimerAnchor] = useState({ baseElapsed: 0, running: false, lastSync: Date.now(), sessionId: null });
  const activeIdRef = useRef(null);

  // Ticker local (suaviza o cronômetro entre os polls)
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  // Nome do campeonato (busca única)
  useEffect(() => {
    if (!championshipId) return;
    championshipApi.get(championshipId).then(({ data }) => {
      if (data?.name) setChampionshipName(data.name);
    }).catch(() => {});
  }, [championshipId]);

  // Polling da fila da mesa — a tela segue automaticamente a luta em andamento
  useEffect(() => {
    if (!championshipId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await scoreboardApi.getSessions(championshipId, { mesa });
        if (cancelled) return;
        setSessions(data.sessions || []);
        const now = Date.now();
        setTimerAnchor((prev) => {
          const srv = (data.sessions || []).find((s) => s.status === 'active') || null;
          if (!srv) return { baseElapsed: 0, running: false, lastSync: now, sessionId: null };
          const srvElapsed = srv.elapsed_seconds || 0;
          const srvRunning = srv.timer_state === 'running';
          let base = srvElapsed;
          // Enquanto o cronômetro segue rodando na MESMA luta, mantém a estimativa
          // contínua local e só adota o valor do servidor se ele estiver à frente
          // (pega o maior). Um snapshot velho do servidor nunca puxa o relógio pra trás.
          if (srvRunning && prev.sessionId === srv._id && prev.running) {
            const localEstimate = prev.baseElapsed + Math.max(0, (now - prev.lastSync) / 1000);
            base = Math.max(localEstimate, srvElapsed);
          }
          return { baseElapsed: base, running: srvRunning, lastSync: now, sessionId: srv._id };
        });
        setSyncing(false);
        setReconnecting(false);
      } catch {
        if (cancelled) return;
        // Nunca deixa a tela "presa" — sinaliza reconexão e mantém o último estado.
        setSyncing(false);
        setReconnecting(true);
      }
    };
    load();
    const t = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [championshipId, mesa]);

  const active = sessions.find((s) => s.status === 'active') || null;
  const nextQueued = sessions.find((s) => s.status === 'queued') || null;

  // Limpar ref ao trocar de luta (swapped agora vem do servidor)
  useEffect(() => {
    if (active && activeIdRef.current !== active._id) {
      activeIdRef.current = active._id;
    }
  }, [active]);

  const patch = useCallback(
    (p) => {
      if (!championshipId || !active) return;
      setSessions((prev) => prev.map((s) => (s._id === active._id ? { ...s, ...p } : s)));
      scoreboardApi.updateSession(championshipId, active._id, p).catch(() => {});
    },
    [championshipId, active]
  );

  const bump = (field, delta) => {
    if (!active) return;
    const next = Math.max(0, (active[field] || 0) + delta);
    setSessions((prev) => prev.map((s) => (s._id === active._id ? { ...s, [field]: next } : s)));
    scoreboardApi
      .updateSession(championshipId, active._id, {
        [field]: next,
        elapsed_seconds: active.elapsed_seconds || 0,
        timer_state: active.timer_state || 'paused',
      })
      .catch(() => {});
  };

  const running = !!active && active.timer_state === 'running';
  const finished = !!active && (active.timer_state === 'finished' || active.status === 'finished' || active.status === 'sent');
  const baseElapsed = timerAnchor.baseElapsed;
  const elapsed = timerAnchor.running ? baseElapsed + Math.max(0, (tick - timerAnchor.lastSync) / 1000) : baseElapsed;
  const remaining = active ? Math.max(0, (active.duration_seconds || 300) - elapsed) : 0;

  const startTimer = () => patch({ timer_state: 'running', status: 'active' });
  const pauseTimer = () => patch({ timer_state: 'paused' });
  const finishFight = () => patch({ timer_state: 'finished', status: 'finished' });

  const boxesFor = (side) => {
    const p =
      side === 'a1'
        ? { score: active.score1 || 0, advantages: active.advantages1 || 0, penalties: active.penalties1 || 0 }
        : { score: active.score2 || 0, advantages: active.advantages2 || 0, penalties: active.penalties2 || 0 };
    return [
      { label: 'PONTOS', field: side === 'a1' ? 'score1' : 'score2', value: p.score, color: side === 'a1' ? 'bg-[#5CB85C]' : 'bg-[#B3B3B3]', darkText: side !== 'a1' },
      { label: 'VANTAGENS', field: side === 'a1' ? 'advantages1' : 'advantages2', value: p.advantages, color: 'bg-[#FFF567]', darkText: true },
      { label: 'PUNIÇÕES', field: side === 'a1' ? 'penalties1' : 'penalties2', value: p.penalties, color: 'bg-[#FF0000]', darkText: false },
    ];
  };

  // ===================== Sem campeonato na URL =====================
  if (!championshipId) {
    return (
      <div className="h-screen w-screen bg-[#0A0A0A] flex flex-col items-center justify-center text-center px-8 select-none">
        <div className="text-[clamp(1.2rem,3vw,3rem)] font-black text-[#D4AF37] uppercase tracking-[0.3em]">
          E-Torneios
        </div>
        <div className="mt-[2vh] text-[clamp(0.9rem,1.8vw,1.8rem)] text-white/70">
          Nenhum campeonato informado. Abra a tela pelo botão <span className="text-[#D4AF37] font-bold">"Abrir Tela"</span> no Placar Eletrônico.
        </div>
      </div>
    );
  }

  // ===================== Frame padrão (sempre visível) =====================
  // Nomes/quadros: dados da luta ativa OU placeholders de "aguardando".
  const swapped = active ? !!active.swapped : false;
  const panelAName = active ? (swapped ? active.athlete2_name : active.athlete1_name) : null;
  const panelATeam = active ? (swapped ? active.athlete2_team : active.athlete1_team) : '';
  const panelBName = active ? (swapped ? active.athlete1_name : active.athlete2_name) : null;
  const panelBTeam = active ? (swapped ? active.athlete1_team : active.athlete2_team) : '';
  const boxesA = active ? (swapped ? boxesFor('a2') : boxesFor('a1')) : ZERO_BOXES('a1');
  const boxesB = active ? (swapped ? boxesFor('a1') : boxesFor('a2')) : ZERO_BOXES('a2');
  const showIdle = !active;

  return (
    <div className="h-screen w-screen overflow-hidden bg-black select-none flex flex-col" data-testid="scoreboard-display">
      {/* Atleta 1 — painel azul */}
      <DisplayAthletePanel
        label={active ? (swapped ? 'Atleta 2' : 'Atleta 1') : 'ATLETA 1'}
        name={formatDisplay(panelAName)}
        team={panelATeam}
        panelColor={active && swapped ? 'bg-[#F5F5F5]' : 'bg-[#1873C0]'}
        textDark={!!active && swapped}
        boxes={boxesA}
        disabled={!active || finished || active.timer_state === 'idle' || (active.timer_state === 'paused' && (active.elapsed_seconds || 0) === 0)}
        onBump={bump}
      />

      {/* Atleta 2 — painel branco */}
      <DisplayAthletePanel
        label={active ? (swapped ? 'Atleta 1' : 'Atleta 2') : 'ATLETA 2'}
        name={formatDisplay(panelBName)}
        team={panelBTeam}
        panelColor={active && swapped ? 'bg-[#1873C0]' : 'bg-[#F5F5F5]'}
        textDark={!active || !swapped}
        boxes={boxesB}
        disabled={!active || finished || active.timer_state === 'idle' || (active.timer_state === 'paused' && (active.elapsed_seconds || 0) === 0)}
        onBump={bump}
      />

      {/* Barra inferior — categoria + controles + cronômetro */}
      <div className="h-[24vh] min-h-[110px] bg-[#18222D] flex items-center gap-[2vw] px-[2vw] relative">
        {syncing && (
          <div className="absolute inset-0 bg-[#18222D]/95 flex items-center justify-center gap-3 z-10">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            <span className="text-[clamp(0.8rem,1.4vw,1.3rem)] font-bold text-white/80 uppercase tracking-widest">Conectando...</span>
          </div>
        )}
        {reconnecting && !syncing && (
          <div className="absolute top-[10%] right-[2vw] z-10 flex items-center gap-2 rounded-full bg-red-500/20 border border-red-500/50 px-4 py-1">
            <Loader2 className="w-4 h-4 text-red-400 animate-spin" />
            <span className="text-[clamp(0.6rem,1vw,0.9rem)] font-bold text-red-300 uppercase tracking-widest">Reconectando...</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-[clamp(0.8rem,1.7vw,1.6rem)] text-white font-bold uppercase tracking-wider truncate">
            {active ? (active.category_label || 'Luta') : (nextQueued ? `Próxima: ${nextQueued.category_label || 'Luta'}` : (championshipName || 'E-Torneios'))}
          </div>
          <div className="text-[clamp(0.55rem,1.1vw,1.05rem)] text-white/50 mt-[0.3vh] truncate">
            {active
              ? `${active.round_label} · Chave #${active.bracket_number || '—'} · Mesa ${mesa}`
              : nextQueued
                ? `${nextQueued.round_label} · Chave #${nextQueued.bracket_number || '—'} · Mesa ${mesa}`
                : `Mesa ${mesa} · ${championshipName || 'E-Torneios'}`}
          </div>
        </div>

        <div className="flex items-center gap-[0.8vw]">
          {!running && !finished && (
            <button
              type="button"
              onClick={startTimer}
              data-testid="display-start"
              className="border-2 border-[#22C55E] text-[#22C55E] bg-transparent rounded-lg font-bold flex items-center gap-2 px-[1.2vw] py-[1vh] text-[clamp(0.7rem,1.4vw,1.3rem)] hover:bg-[#22C55E] hover:text-black transition-colors"
            >
              <Play size={24} /> Começar!
            </button>
          )}
          {running && (
            <button
              type="button"
              onClick={pauseTimer}
              data-testid="display-pause"
              className="border-2 border-[#EAB308] text-[#EAB308] bg-transparent rounded-lg font-bold flex items-center gap-2 px-[1.2vw] py-[1vh] text-[clamp(0.7rem,1.4vw,1.3rem)] hover:bg-[#EAB308] hover:text-black transition-colors"
            >
              <Pause size={24} /> Pausar
            </button>
          )}
          {!finished && (
            <button
              type="button"
              onClick={finishFight}
              disabled={!active || active.timer_state === 'idle' || (active.timer_state === 'paused' && (active.elapsed_seconds || 0) === 0)}
              data-testid="display-finish"
              className="border-2 border-red-500 text-red-500 bg-transparent rounded-lg font-bold px-[1.2vw] py-[1vh] text-[clamp(0.7rem,1.4vw,1.3rem)] hover:bg-red-500 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-default"
            >
              Finalizar luta
            </button>
          )}
          <button
            type="button"
            onClick={() => { if (!active) return; const next = !active.swapped; patch({ swapped: next }); }}
            className="border-2 border-white/70 text-white bg-transparent rounded-lg font-bold flex items-center gap-2 px-[1.2vw] py-[1vh] text-[clamp(0.7rem,1.4vw,1.3rem)] hover:bg-white hover:text-black transition-colors"
          >
            <RefreshCw size={24} /> Trocar cores
          </button>
        </div>

        <div className="flex items-center gap-[1vw]">
          {finished && (
            <span className="text-[clamp(0.8rem,1.6vw,1.5rem)] font-black text-red-500 uppercase tracking-widest">FIM</span>
          )}
          <div
            className={`font-mono font-black tabular-nums text-[clamp(3.6rem,10.5vw,12.75rem)] leading-none ${finished ? 'text-red-500' : active ? 'text-[#D4AF37]' : 'text-[#D4AF37]/40'}`}
            data-testid="display-timer"
          >
            {active ? formatTime(remaining) : formatTime(nextQueued ? nextQueued.duration_seconds : 0)}
          </div>
        </div>
      </div>

      {/* Overlay de "aguardando" — só quando não há luta ativa (nunca tela preta vazia) */}
      {showIdle && !syncing && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20">
          <div className="w-6 h-6 rounded-full bg-[#D4AF37] animate-ping" />
          <div className="mt-[3vh] text-[clamp(1.4rem,3.2vw,3.2rem)] font-bold text-white animate-pulse">
            Aguardando a próxima luta...
          </div>
          {nextQueued && (
            <div className="mt-[2vh] text-[clamp(0.9rem,2vw,2rem)] text-[#A1A1AA]">
              Próxima: <span className="text-white font-bold">{formatDisplay(nextQueued.athlete1_name) || '—'}</span>
              <span className="text-[#D4AF37] font-black mx-3">VS</span>
              <span className="text-white font-bold">{formatDisplay(nextQueued.athlete2_name) || '—'}</span>
              {nextQueued.category_label && <span className="text-[#D4AF37]"> · {nextQueued.category_label}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
